import { Badge, Box, Checkbox, Flex, Tag, Text } from '@chakra-ui/react';
import { PreviewAcceptedInquiry } from '@texas/api/endpoints/offerApi';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { useTranslation } from 'react-i18next';
import { InquiriesByBranch, PreviewBranch } from './createOfferPreview';
import { CreateOfferSection } from './CreateOfferSection';

interface BranchInquirySectionProps {
  awaitingCount: number;
  branches: PreviewBranch[];
  inquiriesByBranch: InquiriesByBranch;
  selectedCount: number;
  selectedIds: number[];
  onToggle: (id: number) => void;
}

export function BranchInquirySection({
  awaitingCount,
  branches,
  inquiriesByBranch,
  selectedCount,
  selectedIds,
  onToggle,
}: BranchInquirySectionProps) {
  const { t } = useTranslation();

  return (
    <CreateOfferSection
      title={t('offer.branchInquiries')}
      description={t('offer.branchInquiriesDescription')}
      meta={t('offer.branchSelectionSummary', {
        selected: selectedCount,
        awaiting: awaitingCount,
      })}
    >
      {branches.length === 0 ? (
        <Box
          bg="offerDrawer.warningBackground"
          border="1px solid"
          borderColor="offerDrawer.warning"
          p={4}
        >
          <Text color="offerDrawer.warningText" fontSize="13px">
            {t('offer.noBranchesConfigured')}
          </Text>
        </Box>
      ) : (
        <Flex direction="column" gap={3}>
          {branches.map((branch) => {
            const branchInquiries =
              inquiriesByBranch.get(branch.branchId) ?? [];
            return branchInquiries.length === 0 ? (
              <UnavailableBranch key={branch.branchId} branch={branch} />
            ) : (
              <AvailableBranch
                key={branch.branchId}
                branch={branch}
                inquiries={branchInquiries}
                selectedIds={selectedIds}
                onToggle={onToggle}
              />
            );
          })}
        </Flex>
      )}
    </CreateOfferSection>
  );
}

function UnavailableBranch({ branch }: { branch: PreviewBranch }) {
  const { t } = useTranslation();

  return (
    <Flex
      align="center"
      bg="offerDrawer.unavailableBackground"
      border="1px dashed"
      borderColor="offerDrawer.unavailableBorder"
      gap={3}
      justify="space-between"
      minH="72px"
      px={4}
      py={3}
    >
      <Flex align="center" gap={3} minW={0}>
        <Flex
          align="center"
          border="1px solid"
          borderColor="offerDrawer.unavailableBorder"
          color="offerDrawer.unavailableText"
          flexShrink={0}
          fontWeight="bold"
          h={8}
          justify="center"
          w={8}
        >
          —
        </Flex>
        <Box minW={0}>
          <Text fontSize="13px" fontWeight="semibold" noOfLines={1}>
            {branch.identifier} · {branch.name}
          </Text>
          <Text color="offerDrawer.mutedText" fontSize="12px">
            {t('offer.noAcceptedInquiryReceived')}
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
}

interface AvailableBranchProps {
  branch: PreviewBranch;
  inquiries: PreviewAcceptedInquiry[];
  selectedIds: number[];
  onToggle: (id: number) => void;
}

function AvailableBranch({
  branch,
  inquiries,
  selectedIds,
  onToggle,
}: AvailableBranchProps) {
  const { t } = useTranslation();

  return (
    <Box border="1px solid" borderColor="offerDrawer.borderStrong">
      <Flex
        align="center"
        bg="offerDrawer.raisedSurface"
        gap={3}
        justify="space-between"
        px={4}
        py={3}
      >
        <Flex align="center" gap={2} minW={0}>
          <Text fontSize="13px" fontWeight="semibold" noOfLines={1}>
            {branch.name}
          </Text>
          <Badge borderRadius={0} fontSize="9px">
            {branch.identifier}
          </Badge>
        </Flex>
        <Text color="offerDrawer.mutedText" flexShrink={0} fontSize="11px">
          {t('offer.acceptedInquiryCount', {
            currency: branch.currencyCode,
            count: inquiries.length,
          })}
        </Text>
      </Flex>
      {inquiries.map((inquiry) => (
        <Checkbox
          key={inquiry.id}
          alignItems="flex-start"
          borderTop="1px solid"
          borderColor="offerDrawer.border"
          colorScheme="orange"
          isChecked={selectedIds.includes(inquiry.id)}
          px={4}
          py={3}
          spacing={3}
          w="full"
          _first={{ borderTop: 0 }}
          onChange={() => onToggle(inquiry.id)}
        >
          <Flex direction="column" gap={1.5} minW={0}>
            <Flex align="center" gap={2} wrap="wrap">
              <Text fontSize="13px" fontWeight="semibold">
                {inquiry.inquiryNumber}
              </Text>
              {inquiry.isMain && (
                <Badge
                  bg="offerDrawer.successBackground"
                  borderRadius={0}
                  color="offerDrawer.success"
                  fontSize="9px"
                >
                  {t('offer.main')}
                </Badge>
              )}
            </Flex>
            <Text color="offerDrawer.mutedText" fontSize="11px">
              {t('offer.inquiryAcceptedSummary', {
                supplier: inquiry.supplierName,
                date: formatDate(Locale.En, inquiry.acceptedDate),
              })}
            </Text>
            <Flex gap={1.5} wrap="wrap">
              {inquiry.tiers.map((tier) => (
                <Tag
                  key={tier.quantity}
                  colorScheme="gray"
                  size="sm"
                  variant="subtle"
                >
                  {tier.quantity.toLocaleString()}
                </Tag>
              ))}
            </Flex>
          </Flex>
        </Checkbox>
      ))}
    </Box>
  );
}
